import {
  EMPTY_ADDRESS,
  EMPTY_PROFILE,
  cepError,
  isUf,
  memberEmailError,
  memberNameError,
  memberPhoneError,
  normalizeAddress,
  normalizeEmail,
  phoneDigits,
  showsOriginChurch,
} from './members';
import type { AvailState, Member, Registration, RegistrationData } from './types';

/**
 * Cadastro público: a pessoa preenche o formulário pelo link da igreja, em 4 etapas, e o
 * Administrador aprova (o membro é criado com os dados enviados) ou recusa. O convite de
 * acesso não sai sozinho: é enviado pela tela Acessos.
 */

export const REGISTRATION_STEPS = ['Seus dados', 'Endereço', 'Vida na igreja', 'Revisão'] as const;

export type RegistrationStep = 0 | 1 | 2 | 3;

export const CONSENT_TEXT =
  'Autorizo a igreja a armazenar e usar estes dados para o cadastro de membros e a organização das ' +
  'escalas, conforme a Lei Geral de Proteção de Dados (LGPD). Posso pedir a correção ou exclusão a ' +
  'qualquer momento.';

export function emptyRegistrationData(): RegistrationData {
  return {
    name: '',
    birthDate: null,
    sex: null,
    civilStatus: null,
    profession: '',
    phone: '',
    email: '',
    address: { ...EMPTY_ADDRESS },
    churchStatus: null,
    memberSince: null,
    joinedVia: null,
    baptismDate: null,
    originChurch: '',
    ministryIds: [],
    notes: '',
  };
}

export type RegistrationField =
  'name' | 'birthDate' | 'sex' | 'phone' | 'email' | 'cep' | 'uf' | 'memberSince' | 'baptismDate' | 'notes';

export type RegistrationErrors = Partial<Record<RegistrationField, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const NOTES_MAX = 1000;

/** Os erros de uma etapa do formulário (a revisão confere todas). */
export function registrationStepErrors(
  step: RegistrationStep,
  data: RegistrationData,
  today: string
): RegistrationErrors {
  const errors: RegistrationErrors = {};

  if (step === 0 || step === 3) {
    const phone = memberPhoneError(data.phone);
    const email = normalizeEmail(data.email);

    if (!data.name.trim()) errors.name = 'Informe seu nome completo.';
    else if (data.name.trim().length > 120) errors.name = 'Use no máximo 120 caracteres.';
    if (!data.birthDate) errors.birthDate = 'Informe sua data de nascimento.';
    else if (data.birthDate > today) errors.birthDate = 'A data não pode ser no futuro.';
    if (!data.sex) errors.sex = 'Escolha uma opção.';
    if (phone) errors.phone = phone;
    if (email && (!EMAIL.test(email) || email.length > 254)) errors.email = 'Informe um e-mail válido.';
    if (!phoneDigits(data.phone) && !email) {
      errors.phone ??= 'Informe ao menos um contato: celular ou e-mail.';
      errors.email ??= 'Informe ao menos um contato: celular ou e-mail.';
    }
  }

  if (step === 1 || step === 3) {
    const cep = cepError(data.address.cep);

    if (cep) errors.cep = cep;
    if (!isUf(data.address.uf)) errors.uf = 'Escolha uma UF válida.';
  }

  if (step === 2 || step === 3) {
    if (data.memberSince && data.memberSince > today) errors.memberSince = 'A data não pode ser no futuro.';
    if (data.baptismDate && data.baptismDate > today) errors.baptismDate = 'A data não pode ser no futuro.';
    if (data.notes.length > NOTES_MAX) errors.notes = `Use no máximo ${NOTES_MAX} caracteres.`;
  }

  return errors;
}

/** Todos os erros do cadastro (o que o servidor confere ao receber). */
export function registrationErrors(data: RegistrationData, today: string): RegistrationErrors {
  return registrationStepErrors(3, data, today);
}

/** Os dados como são guardados: e-mail em minúsculas, telefone e CEP só com dígitos. */
export function normalizeRegistration(data: RegistrationData): RegistrationData {
  return {
    ...data,
    name: data.name.trim().replace(/\s+/g, ' '),
    profession: data.profession.trim(),
    phone: phoneDigits(data.phone),
    email: normalizeEmail(data.email),
    address: normalizeAddress(data.address),
    originChurch: showsOriginChurch(data.joinedVia) ? data.originChurch.trim() : '',
    ministryIds: [...new Set(data.ministryIds)],
    notes: data.notes.trim(),
  };
}

/**
 * Por que a aprovação não pode criar o membro: o nome ou o e-mail já são de outro membro
 * (são únicos na base). `null` quando dá para aprovar.
 */
export function approvalError(registration: Registration, members: readonly Member[]): string | null {
  if (registration.status !== 'pending') return 'Esta solicitação já foi analisada.';

  const name = memberNameError(members, '', registration.data.name);

  if (name) return `${name} Vincule ao cadastro existente ou recuse a solicitação.`;

  const email = memberEmailError(members, '', registration.data.email);

  if (email) return `${email} Vincule ao cadastro existente ou recuse a solicitação.`;

  return null;
}

/**
 * O membro criado na aprovação: os dados enviados e a foto, sem funções (os líderes
 * habilitam depois).
 */
export function memberFromRegistration(registration: Registration, memberId: string): Member {
  const d = normalizeRegistration(registration.data);

  return {
    id: memberId,
    inactiveSince: null,
    selfUpdatedAt: null,
    admin: false,
    photo: registration.photo,
    name: d.name,
    nickname: '',
    email: d.email,
    phone: d.phone,
    birthDate: d.birthDate,
    sex: d.sex ?? 'F',
    spouseId: null,
    spouseText: '',
    coupleMode: null,
    roles: {},
    availability: Array<AvailState>(21).fill('unset'),
    blackouts: [],
    ...EMPTY_PROFILE,
    civilStatus: d.civilStatus,
    profession: d.profession,
    churchStatus: d.churchStatus,
    memberSince: d.memberSince,
    joinedVia: d.joinedVia,
    baptismDate: d.baptismDate,
    originChurch: d.originChurch,
    address: d.address,
  };
}

export function pendingRegistrations(registrations: readonly Registration[]): Registration[] {
  return registrations.filter((r) => r.status === 'pending').sort((a, b) => a.submittedAt - b.submittedAt);
}

/** Aprovadas e recusadas, da mais recente para a mais antiga (o Histórico). */
export function decidedRegistrations(registrations: readonly Registration[]): Registration[] {
  return registrations
    .filter((r) => r.status !== 'pending')
    .sort((a, b) => (b.decidedAt ?? 0) - (a.decidedAt ?? 0));
}

/** Caminho do formulário público: `/{slug}/cadastro/{token}`. */
export function registrationPath(slug: string, token: string): string {
  return `/${slug}/cadastro/${token}`;
}

/** Mensagem pronta para mandar o link pelo WhatsApp. */
export function registrationInvite(orgName: string, url: string): string {
  return `Olá! Faça seu cadastro de membro da ${orgName} pelo link: ${url}\nLeva cerca de 3 minutos.`;
}
